import { Info as InfoIcon, Sync as SyncIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import PartnerCommissionApi from "../../../infrastructure/api/partnerCommissionApi";
import JalaliDatePicker from "../../components/shared/jalali-datepicker";
import {
  getEnumLabel,
  toSelectOptions,
  TRIGGER_EVENT_LABELS,
} from "../SLN/market-growth/constants/commissionEnums";
import { formatApiDate } from "../SLN/market-growth/utils/commissionValidation";
import CommissionDetailDialog from "./components/CommissionDetailDialog";
import PartnerCommissionPageShell from "./components/PartnerCommissionPageShell";
import {
  PARTNER_COMMISSION_STATUS_LABELS,
  getCommissionStatusChipColor,
} from "./constants/portalEnums";
import { formatMoney } from "./utils/formatMoney";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const PartnerCommissionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noPartnerProfile, setNoPartnerProfile] = useState(false);
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    triggerEvent: "",
    fromDate: null,
    toDate: null,
  });
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoPartnerProfile(false);
    try {
      const params = { page: 1, pageSize: 50 };
      if (filters.status) params.status = filters.status;
      if (filters.triggerEvent) params.triggerEvent = filters.triggerEvent;
      if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
      if (filters.toDate) params.toDate = formatApiDate(filters.toDate);

      const res = await PartnerCommissionApi.getCommissions(params);
      const normalized = PartnerCommissionApi.normalize(
        res,
        "بارگذاری پورسانت‌ها ناموفق بود."
      );
      if (normalized.noPartnerProfile) {
        setNoPartnerProfile(true);
        setItems([]);
        return;
      }
      if (normalized.error) {
        setError(normalized.error);
        setItems([]);
        return;
      }
      const pageData = normalized.data;
      setItems(Array.isArray(pageData?.items) ? pageData.items : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PartnerCommissionPageShell
      title="پورسانت‌های من"
      description="لیست پورسانت‌های محاسبه‌شده."
      loading={loading}
      error={error}
      noPartnerProfile={noPartnerProfile}
      onRetry={load}
    >
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  value={filters.status}
                  label="وضعیت"
                  onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(PARTNER_COMMISSION_STATUS_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>رویداد</InputLabel>
                <Select
                  value={filters.triggerEvent}
                  label="رویداد"
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, triggerEvent: e.target.value }))
                  }
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(TRIGGER_EVENT_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    fromDate: value ? new Date(value) : null,
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    toDate: value ? new Date(value) : null,
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" onClick={load} fullWidth>
                  اعمال فیلتر
                </Button>
                <Tooltip title="بارگذاری مجدد">
                  <IconButton onClick={load}>
                    <SyncIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent sx={{ overflowX: "auto", p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>رویداد</TableCell>
                <TableCell>پروژه</TableCell>
                <TableCell>گروه</TableCell>
                <TableCell>قانون</TableCell>
                <TableCell>مبلغ مبنا</TableCell>
                <TableCell>پورسانت</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>تاریخ</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    پورسانتی یافت نشد.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.commissionId} hover>
                    <TableCell>
                      {getEnumLabel(TRIGGER_EVENT_LABELS, row.triggerEvent)}
                    </TableCell>
                    <TableCell>{row.relatedProjectId ?? "—"}</TableCell>
                    <TableCell>{row.commissionGroupTitle || "—"}</TableCell>
                    <TableCell>{row.ruleTitle || "—"}</TableCell>
                    <TableCell>{formatMoney(row.baseAmount)}</TableCell>
                    <TableCell>{formatMoney(row.commissionAmount)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getEnumLabel(PARTNER_COMMISSION_STATUS_LABELS, row.status)}
                        color={getCommissionStatusChipColor(row.status)}
                      />
                    </TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="جزئیات">
                        <IconButton
                          size="small"
                          onClick={() => setDetailId(row.commissionId)}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CommissionDetailDialog
        open={Boolean(detailId)}
        onClose={() => setDetailId(null)}
        commissionId={detailId}
      />
    </PartnerCommissionPageShell>
  );
};

export default PartnerCommissionsPage;
